import * as React from "react";
import { useContext } from "react";
import { AvailableLanguagesResponse, LanguageInfo } from "./LanguageSelector";
import axios from "axios";

export interface Translations {
  [key: string]: string;
}

type LanguageContextType = {
  translate: (key: string, params?: { [key: string]: string }) => string;
  availableLanguages: LanguageInfo[];
  changeLanguageCode: (code: LanguageInfo["code"]) => void;
  languageCode: LanguageInfo["code"];
  dictionary: Translations;
};

export interface Language {
  languageInfo: LanguageInfo;
  dictionary: Translations;
}

const LanguageContext = React.createContext<LanguageContextType>(null);

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
}

type LanguageProviderProps = {};

export const AvailableLanguagesHolder = (() => {
  let availableLanguages: LanguageInfo[] = null;

  const subscribers: ((availableLanguages: LanguageInfo[]) => void)[] = [];

  const fetchAllLanguages = () => {
    axios.get<AvailableLanguagesResponse>(`/languages`).then(({ data }) => {
      availableLanguages = data.availableLanguages;
      subscribers.forEach((sub) => sub([...availableLanguages]));
    });
  };

  const subscribe = (
    subscriber: (availableLanguages: LanguageInfo[]) => void
  ) => {
    subscribers.push(subscriber);

    if (availableLanguages) {
      subscriber([...availableLanguages]);
    } else {
      fetchAllLanguages();
    }

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }
    };
  };

  return {
    subscribe,
  };
})();

export const LanguageHolder = (() => {

  class LanguageService {
    private languageMap: Map<LanguageInfo["code"], Language> = new Map();
    private _languageCode =
      document.documentElement.lang === "en"
        ? "en_US"
        : document.documentElement.lang;
    private subscribers: ((language: Language) => void)[] = [];

    get dictionary() {
      if(this.languageMap.has(this._languageCode)){
        return this.languageMap.get(this._languageCode).dictionary;
      }
      return null;
    }

    get languageCode(){
      return this._languageCode;
    }

    async fetchLanguage(forceUpdate: boolean = false) {

      const languageCode = this._languageCode;
      if(this.languageMap.has(languageCode) && !forceUpdate) {
        return this.languageMap.get(languageCode);
      }
  
      // because this call not only gets the language, but also sets it in the backend context we need to ensure we do not get a 304
      const { data } = await axios.get<Language>(`/language/${languageCode}`, {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        params: {
          _t: new Date().getTime(), // Add a timestamp to the request to make it unique
        },
      });
      this.languageMap.set(languageCode, data);
      this.subscribers.forEach((sub) => sub({ ...data }));
      return { ...data };
    };
  
    public subscribe(subscriber: (language: Language) => void) {
      this.subscribers.push(subscriber);
  
      if (this.languageMap.has(this._languageCode)) {
        subscriber({ ...this.languageMap.get(this._languageCode) });
      } else {
        this.fetchLanguage();
      }
  
      // Return an unsubscribe function
      return () => {
        const index = this.subscribers.indexOf(subscriber);
        if (index !== -1) {
          this.subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
        }
      };
    };
  
    public async updateLanguageCode(
      newLanguageCode: LanguageInfo["code"]
    ): Promise<void> {
      if (this._languageCode !== newLanguageCode) {
        document.documentElement.lang = newLanguageCode;
        this._languageCode = newLanguageCode;
        await this.fetchLanguage(true);
      }
      return;
    };
  }

  return new LanguageService();

})();

export function LanguageProvider({
  children,
}: React.PropsWithChildren<LanguageProviderProps>) {
  const [dictionary, setDictionary] = React.useState<Translations>(LanguageHolder.dictionary);
  const [availableLanguages, setAvailableLanguages] =
    React.useState<LanguageInfo[]>(null);
  const [languageCode, setLanguageCode] = React.useState<LanguageInfo["code"]>(
    LanguageHolder.languageCode
  );

  React.useEffect(() => {
    const unsubscibeLanguageHolder = LanguageHolder.subscribe((language) => {
      setDictionary(language.dictionary);
      setLanguageCode(language.languageInfo.code);
    });

    const unsubscribeAvailableLanguagesHolder =
      AvailableLanguagesHolder.subscribe((availableLanguages) => {
        setAvailableLanguages(availableLanguages);
      });

    return () => {
      unsubscribeAvailableLanguagesHolder();
      unsubscibeLanguageHolder();
    };
  }, []);

  const translate = React.useCallback(
    (key: string, params?: { [key: string]: string }): string => {
      let translation = (dictionary && dictionary[key]) || key; // Fallback to key if tranlation not found

      if (params) {
        Object.keys(params).forEach((placeholder) => {
          const regex = new RegExp(`%${placeholder}%`, "g");
          translation = translation.replace(regex, params[placeholder]);
        });
      }

      return translation;
    },
    [dictionary]
  );

  async function changeLanguageCode(code: LanguageInfo["code"]): Promise<void> {
    // check against available languages?
    setLanguageCode(code);
    await LanguageHolder.updateLanguageCode(code);
    // wait for the new language to be set on the backend then refresh the page
    window.location.reload(); // once everything is in React, we won't need to refresh
  }

  const contextValue = {
    translate,
    availableLanguages,
    changeLanguageCode,
    languageCode,
    dictionary
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}
